<template>
  <div class="page-body">
    <div class="container-fluid">
      <div class="edit-profile">
        <div class="row">
          <div class="col-12 col-xl-12">
            <div class="card">
              <!-- Header -->
              <div class="card-header">
                <div class="text-neutral-500 text-xl font-medium font-['Poppins'] leading-normal">Perfil</div>
                <div class="card-options">
                  <a class="card-options-collapse" href="#" data-bs-toggle="card-collapse">
                    <i class="fe fe-chevron-up"></i>
                  </a>
                  <a class="card-options-remove" href="#" data-bs-toggle="card-remove">
                    <i class="fe fe-x"></i>
                  </a>
                </div>
              </div>

              <!-- Body -->
              <div class="card-body" style="margin-top: 20px;">
                <form @submit.prevent="submitForm">
                  <!-- Primera fila: Nombre, Apellido Paterno, Apellido Materno -->
                  <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <!-- Nombre -->
                    <div class="relative">
                      <label for="name" class="block text-sm font-medium text-gray-700 mb-2">
                        Nombre
                      </label>
                      <input 
                        v-model="form.name" 
                        type="text" 
                        id="name" 
                        class="w-full px-4 py-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none text-sm"
                        placeholder="Nombre"
                        @blur="validateName"
                      >
                      <!-- Alerta -->
                      <div v-if="showNameAlert" class="absolute left-0 top-full mt-2 z-10 flex items-center gap-2 bg-white px-3 py-2 rounded-md shadow-lg border border-gray-200 text-sm whitespace-nowrap">
                        <div class="flex-shrink-0 w-6 h-6 bg-orange-500 rounded flex items-center justify-center text-white font-bold">!</div>
                        <span class="text-gray-700">Completa este campo</span>
                      </div>
                    </div>

                    <!-- Apellido Paterno -->
                    <div class="relative">
                      <label for="last_name" class="block text-sm font-medium text-gray-700 mb-2">
                        Apellido Paterno
                      </label>
                      <input 
                        v-model="form.last_name" 
                        type="text" 
                        id="last_name" 
                        class="w-full px-4 py-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none text-sm"
                        placeholder="Primer apellido"
                        @blur="validateLastName"
                      >
                      <!-- Alerta -->
                      <div v-if="showLastNameAlert" class="absolute left-0 top-full mt-2 z-10 flex items-center gap-2 bg-white px-3 py-2 rounded-md shadow-lg border border-gray-200 text-sm whitespace-nowrap">
                        <div class="flex-shrink-0 w-6 h-6 bg-orange-500 rounded flex items-center justify-center text-white font-bold">!</div>
                        <span class="text-gray-700">Completa este campo</span>
                      </div>
                    </div>

                    <!-- Apellido Materno -->
                    <div class="relative">
                      <label for="last_second_name" class="block text-sm font-medium text-gray-700 mb-2">
                        Apellido Materno
                      </label>
                      <input 
                        v-model="form.last_second_name" 
                        type="text" 
                        id="last_second_name" 
                        class="w-full px-4 py-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none text-sm"
                        placeholder="Segundo apellido"
                        @blur="validateSecondLastName"
                      >
                      <!-- Alerta -->
                      <div v-if="showSecondLastNameAlert" class="absolute left-0 top-full mt-2 z-10 flex items-center gap-2 bg-white px-3 py-2 rounded-md shadow-lg border border-gray-200 text-sm whitespace-nowrap">
                        <div class="flex-shrink-0 w-6 h-6 bg-orange-500 rounded flex items-center justify-center text-white font-bold">!</div>
                        <span class="text-gray-700">Completa este campo</span>
                      </div>
                    </div>
                  </div>

                  <!-- Segunda fila: Teléfono y Compañía -->
                  <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <!-- Teléfono -->
                    <div class="relative">
                      <label for="phone" class="block text-sm font-medium text-gray-700 mb-2">
                        Teléfono
                      </label>
                      
                      <!-- Country selector -->
                      <div class="relative mb-3">
                        <div 
                          @click.stop="toggleDropdown"
                          class="w-full px-4 py-2.5 border border-gray-300 rounded-md cursor-pointer flex items-center justify-between hover:border-gray-400 transition-colors bg-white"
                        >
                          <div v-if="selectedCountry" class="flex items-center gap-2">
                            <img 
                              v-if="getSelectedCountry && getSelectedCountry.flag" 
                              :src="getSelectedCountry.flag" 
                              :alt="`Bandera de ${getSelectedCountry.name}`" 
                              class="w-6 h-4 object-cover border border-gray-300 rounded"
                            />
                            <span class="text-sm text-gray-700">{{ getSelectedCountry ? getSelectedCountry.name : '' }} ({{ selectedCountry }})</span>
                          </div>
                          <div v-else class="text-sm text-gray-500">Selecciona un país</div>
                          <svg class="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                          </svg>
                        </div>

                        <!-- Dropdown -->
                        <div v-if="isDropdownOpen" class="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto z-20">
                          <div 
                            v-for="country in filteredCountries" 
                            :key="country.dial_code"
                            @click="selectCountry(country.dial_code)"
                            class="px-4 py-2.5 hover:bg-gray-50 cursor-pointer flex items-center gap-2 text-sm transition-colors"
                          >
                            <img 
                              v-if="country.flag" 
                              :src="country.flag" 
                              :alt="`Bandera de ${country.name}`" 
                              class="w-6 h-4 object-cover border border-gray-300 rounded"
                            />
                            <span class="text-gray-700">{{ country.name }} ({{ country.dial_code }})</span>
                          </div>
                        </div>
                      </div>

                      <!-- Phone input -->
                      <input 
                        v-model="form.phone" 
                        type="tel" 
                        id="phone" 
                        :class="[
                          'w-full px-4 py-2.5 rounded-md focus:ring-2 focus:ring-blue-500 transition-all outline-none text-sm',
                          phoneError ? 'border-red-300 bg-red-50 text-red-900' : 'border border-gray-300 focus:border-blue-500'
                        ]"
                        placeholder="Ingresa tu número de teléfono" 
                        @input="onlyNumbers"
                        @blur="validatePhoneAndCheck"
                        maxlength="10"
                      />
                      
                      <p v-if="phoneError" class="mt-1 text-sm text-red-600">{{ phoneError }}</p>
                      
                      <!-- Alerta -->
                      <div v-if="showPhoneAlert" class="absolute left-0 top-full mt-2 z-10 flex items-center gap-2 bg-white px-3 py-2 rounded-md shadow-lg border border-gray-200 text-sm max-w-xs">
                        <div class="flex-shrink-0 w-6 h-6 bg-orange-500 rounded flex items-center justify-center text-white font-bold">!</div>
                        <span class="text-gray-700">{{ phoneError || 'Completa este campo' }}</span>
                      </div>
                    </div>

                    <!-- Compañía -->
                    <div class="relative">
                      <label for="company" class="block text-sm font-medium text-gray-700 mb-2">
                        Compañía
                      </label>
                      <select 
                        v-model="form.company_id" 
                        id="company"
                        class="w-full px-4 py-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none text-sm bg-white"
                        @change="validateCompany"
                      >
                        <option value="0">Selecciona una compañía</option>
                        <option v-for="company in companies" :key="company.id" :value="company.id">
                          {{ company.name }}
                        </option>
                      </select>
                      
                      <!-- Alerta -->
                      <div v-if="showCompanyAlert" class="absolute left-0 top-full mt-2 z-10 flex items-center gap-2 bg-white px-3 py-2 rounded-md shadow-lg border border-gray-200 text-sm whitespace-nowrap">
                        <div class="flex-shrink-0 w-6 h-6 bg-orange-500 rounded flex items-center justify-center text-white font-bold">!</div>
                        <span class="text-gray-700">Selecciona una compañía</span>
                      </div>
                    </div>
                    <div class="relative">
                      <label for="email" class="block text-sm font-medium text-gray-700 mb-2">
                        Email
                      </label>
                      <input 
                        v-model="form.email" 
                        type="email" 
                        id="email" 
                        class="w-full px-4 py-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none text-sm"
                        placeholder="Ingrese su email"
                        @blur="validateEmail"
                      >
                      
                      <!-- Alerta -->
                      <div v-if="showEmailAlert" class="absolute left-0 top-full mt-2 z-10 flex items-center gap-2 bg-white px-3 py-2 rounded-md shadow-lg border border-gray-200 text-sm max-w-xs">
                        <div class="flex-shrink-0 w-6 h-6 bg-orange-500 rounded flex items-center justify-center text-white font-bold">!</div>
                        <span class="text-gray-700">{{ emailError || 'Ingresa un email válido' }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- Botones -->
                  <div class="form-footer text-end">
                    <button type="button" class="cancel-btn" @click="goBack">
                      <span class="cancel-btn-text">Cancelar</span>
                    </button>
                    <button class="custom-create-btn" :disabled="isSubmitting">
                      <span class="custom-create-btn-text">
                        {{ isSubmitting ? 'Guardando...' : 'Guardar' }}
                      </span>
                    </button>
                  </div>
                </form>
              </div>

              <!-- Mensajes de éxito/error -->
              <div v-if="successMessage" class="user-alert">
                <div class="user-alert-content">
                  <div class="user-alert-text">
                    {{ successMessage }}
                  </div>
                </div>
                <div class="w-4 h-4 relative"></div>
              </div>

              <div v-if="errorMessage" class="user-alert-error">
                <div class="user-alert-content">
                  <div class="user-alert-text">
                    {{ errorMessage }}
                  </div>
                </div>
                <div class="w-4 h-4 relative"></div>
              </div>

              <head>
                <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600&display=swap" rel="stylesheet">
              </head>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { ref, computed, onMounted } from "vue";
import { useStore } from 'vuex';
import { useRouter } from "vue-router";

export default {
  setup() {
    const store = useStore();
    const session = computed(() => store.getters.getUserSession);
    const successMessage = ref('');
    const errorMessage = ref('');
    const router = useRouter();
    const showNameAlert = ref(false);
    const showLastNameAlert = ref(false);
    const showSecondLastNameAlert = ref(false);
    const showPhoneAlert = ref(false);
    const showCompanyAlert = ref(false);
    const showEmailAlert = ref(false);
    const emailError = ref("");
    
    const countryCodes = ref([]);
    const selectedCountry = ref("");
    const isDropdownOpen = ref(false);
    const phoneError = ref("");
    const isSubmitting = ref(false);

    const form = ref({
      email: '',
      name: '',
      last_name: '',
      last_second_name: '',
      phone: '',
      country_code: '',
      rol: '3',
      company_id: null,
    });

    let companies = ref([]);

    const validateName = () => {
      if (form.value.name.trim() !== '') {
        showNameAlert.value = false;
      }
    };

    const validateLastName = () => {
      if (form.value.last_name.trim() !== '') {
        showLastNameAlert.value = false;
      }
    };

    const validateSecondLastName = () => {
      if (form.value.last_second_name.trim() !== '') {
        showSecondLastNameAlert.value = false;
      }
    };

    const validateCompany = () => {
      if (form.value.company_id && form.value.company_id !== 0) {
        showCompanyAlert.value = false;
      }
    };

    const validateEmail = () => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!form.value.email.includes('@')) {
        showEmailAlert.value = true;
        emailError.value = "El correo debe contener un '@'";
      } else if (!emailRegex.test(form.value.email)) {
        showEmailAlert.value = true;
        emailError.value = "Ingresa un email válido";
      } else {
        showEmailAlert.value = false;
        emailError.value = "";
      }
    };

    const validatePhoneFormat = () => {
      phoneError.value = '';
      
      if (!form.value.phone) {
        phoneError.value = 'Campo obligatorio';
        return false;
      }

      const phoneRegex = /^\d{10}$/;
      if (!phoneRegex.test(form.value.phone)) {
        phoneError.value = 'El número debe tener exactamente 10 dígitos';
        return false;
      }

      return true;
    };

    const getSelectedCountry = computed(() => {
      return countryCodes.value.find(country => country.dial_code === selectedCountry.value);
    });

    const filteredCountries = computed(() => {
      return [...countryCodes.value].sort((a, b) => a.name.localeCompare(b.name));
    });

    const closeDropdown = (e) => {
      const container = document.querySelector('.custom-select-container');
      if (container && !container.contains(e.target)) {
        isDropdownOpen.value = false;
      }
    };

    const toggleDropdown = () => {
      isDropdownOpen.value = !isDropdownOpen.value;
    };

    const selectCountry = (dialCode) => {
      selectedCountry.value = dialCode;
      form.value.country_code = dialCode;
      isDropdownOpen.value = false;
    };

    const onlyNumbers = (event) => {
      form.value.phone = event.target.value.replace(/\D/g, '');
    };

    const checkPhoneExists = async () => {
      if (!validatePhoneFormat()) {
        return { exists: false, error: true };
      }

      if (!selectedCountry.value) {
        phoneError.value = 'Debes seleccionar un país';
        return { exists: false, error: true };
      }

      const token = session.value.token_api;
      const completePhone = form.value.phone;
      const countryCode = selectedCountry.value;
      
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/broker/check-phone-exists`,
          {
            params: {
              phone: completePhone,
              country_code: countryCode,
            },
            headers: {
              Authorization: `Bearer ${token.trim()}`,
            },
          }
        );
        
        return { exists: response.data.exists, error: false, message: response.data.message };
      } catch (err) {
        console.error("Error al verificar el teléfono:", err);
        phoneError.value = err.response?.data?.message || "Error al verificar el teléfono.";
        return { exists: false, error: true, message: phoneError.value };
      }
    };

    const validatePhoneAndCheck = async () => {
      if (!validatePhoneFormat()) {
        return;
      }

      if (!selectedCountry.value) {
        phoneError.value = 'Debes seleccionar un país';
        return;
      }

      const { exists, error: checkError, message } = await checkPhoneExists();
      
      if (checkError) {
        return;
      }
      
      if (exists) {
        phoneError.value = message || "El número de teléfono ya está registrado. Por favor, utiliza otro número.";
      }
    };

    const loadCountryCodes = async () => {
      const token = session.value ? session.value.token_api : null;

      if (!token) {
        phoneError.value = "No se encontró la sesión del usuario. Vuelve a iniciar sesión.";
        return;
      }

      const url = `${process.env.VUE_APP_API_BASE_URL}/get-country-preferences`;
      const options = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token.trim()}`,
        },
      };

      try {
        const response = await axios.get(url, options);
        countryCodes.value = response.data;

        if (countryCodes.value.length > 0) {
          const mexicoCountry = countryCodes.value.find(c => c.dial_code === '+52');
          
          if (mexicoCountry) {
            selectedCountry.value = '+52';
            form.value.country_code = '+52';
          } else {
            selectedCountry.value = countryCodes.value[0].dial_code;
            form.value.country_code = countryCodes.value[0].dial_code;
          }
        }
      } catch (err) {
        console.error("Error al cargar códigos de país:", err);
        phoneError.value = "No se pudieron cargar los códigos de país.";
      }
    };

    async function fetchCompanies() {
      try {
        let options = {
          headers: {
            'Authorization': `Bearer ${session.value.token_api}`
          }
        };
        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/companies`, options
        );

        if (response.status === 401) {
          store.dispatch('clearUserSession');
        }

        if (response.status === 200 && response.data?.resp?.companies) {
          companies.value = response.data.resp.companies;
        }
      } catch (error) {
        console.error("Error al obtener las compañías:", error);
      }
    }

    onMounted(() => {
      fetchCompanies();
      loadCountryCodes();
      document.addEventListener('click', closeDropdown);
    });
    
    const submitForm = async () => {
      showNameAlert.value = false;
      showLastNameAlert.value = false;
      showSecondLastNameAlert.value = false;
      showPhoneAlert.value = false;
      showCompanyAlert.value = false;
      showEmailAlert.value = false;

      if (form.value.name.trim() === '') {
        showNameAlert.value = true;
        return;
      }
      
      if (form.value.last_name.trim() === '') {
        showLastNameAlert.value = true;
        return;
      }
      
      if (form.value.last_second_name.trim() === '') {
        showSecondLastNameAlert.value = true;
        return;
      }
      
      if (!validatePhoneFormat()) {
        showPhoneAlert.value = true;
        return;
      }
      
      if (!form.value.company_id || form.value.company_id === 0) {
        showCompanyAlert.value = true;
        return;
      }
      
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.value.email)) {
        showEmailAlert.value = true;
        return;
      }

      const { exists, error: checkError, message } = await checkPhoneExists();
      
      if (checkError) {
        return;
      }
      
      if (exists) {
        phoneError.value = message || "El número de teléfono ya está registrado. Por favor, utiliza otro número.";
        showPhoneAlert.value = true;
        return;
      }

      if (!selectedCountry.value) {
        phoneError.value = 'Debes seleccionar un país';
        showPhoneAlert.value = true;
        return;
      }

      isSubmitting.value = true;
      
      const token = session.value.token_api;

      if (!token) {
        showToast('No se ha encontrado el token de autenticación', 'error');
        isSubmitting.value = false;
        return;
      }

      form.value.country_code = selectedCountry.value;
      
      axios.post(`${process.env.VUE_APP_API_BASE_URL}/broker/profile/create_new_account`, form.value, {
        headers: {
          'Authorization': `Bearer ${token}`
        } 
      })
      
      .then(() => {
        showToast('Usuario creado con éxito', 'success');
        successMessage.value = 'Usuario creado con éxito';

        setTimeout(() => {
          router.push({ name: "advisorList" });
        }, 2000);
      })
      
      .catch(error => {
        if (error.response && error.response.status === 401) {
          showToast('Sesión expirada. Por favor, vuelve a iniciar sesión.', 'error');
          store.dispatch('clearUserSession');
        } else if (error.response && error.response.status === 409) {
          const errorMsg = error.response.data.message || "El número de teléfono ya está registrado.";
          phoneError.value = errorMsg;
          showToast(errorMsg, 'error');
        } else if (error.response && error.response.status === 422 && error.response.data.errors) {
          const errors = error.response.data.errors;
          for (const field in errors) {
            if (Object.prototype.hasOwnProperty.call(errors, field)) {
              errors[field].forEach(msg => showToast(msg, 'error'));
            }
          }
        } else {
          const errorMsg = error.response?.data?.message || error.message || 'Ha ocurrido un error inesperado';
          showToast(errorMsg, 'error');
        }
      })
      .finally(() => {
        isSubmitting.value = false;
      });
    };

    const showToast = (message, type = 'success') => {
      if (type === 'success') {
        successMessage.value = message;
        setTimeout(() => {
          successMessage.value = '';
        }, 3000);
      } else if (type === 'error') {
        errorMessage.value = message;
        setTimeout(() => {
          errorMessage.value = '';
        }, 3000);
      }
    };

    const goBack = () => {
      router.go(-1);
    };

    
    return {
      session,
      form,
      submitForm,
      showToast,
      companies,
      successMessage,
      errorMessage, 
      countryCodes,
      selectedCountry,
      isDropdownOpen,
      toggleDropdown,
      selectCountry,
      getSelectedCountry,
      filteredCountries,
      onlyNumbers,
      validatePhoneAndCheck,
      phoneError,
      isSubmitting,
      goBack,
      showNameAlert,
      showLastNameAlert,
      showSecondLastNameAlert,
      showPhoneAlert,
      showCompanyAlert,
      showEmailAlert,
      validateName,
      validateLastName,
      validateSecondLastName,
      validatePhoneFormat,
      validateCompany,
      validateEmail,
      emailError,
    };
  }
};
</script>

<style scoped>
/* Mantener estilos de Bootstrap y personalizados que necesitas */
.user-alert {
  background-color: #d4edda;
  border: 1px solid #c3e6cb;
  color: #155724;
  padding: 10px;
  border-radius: 5px;
  margin-top: 10px;
}

.user-alert-error {
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  color: #721c24;
  padding: 10px;
  border-radius: 5px;
  margin-top: 10px;
}

.custom-create-btn {
  padding: 0.5rem 1.5rem;
  color: white;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: background-color 0.15s ease-in-out;
}

.custom-create-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.cancel-btn {
  padding: 0.5rem 1.5rem;
  background-color: #6c757d;
  color: white;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: background-color 0.15s ease-in-out;
  margin-right: 10px;
}

.cancel-btn:hover {
  background-color: #5a6268;
}

.cancel-btn-text {
  font-family: 'Poppins', sans-serif;
  font-weight: 400;
}

.form-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

/* Media queries para responsividad */
@media (max-width: 768px) {
  .form-footer {
    flex-direction: column;
  }
  
  .cancel-btn, .custom-create-btn {
    width: 100%; 
    margin-right: 0; 
    margin-bottom: 10px; 
  }

  .cancel-btn:last-child, 
  .custom-create-btn:last-child {
    margin-bottom: 0; 
  }
}
</style>